/**
 * Mobile-Navigation (Drawer) + Tabellen-Labels für die Kartenansicht.
 *
 * DESKTOP-GARANTIE: Dieses Modul verändert auf Desktop nichts Sichtbares.
 * - Der Burger-Button und das Overlay sind per CSS ab 1025px display:none.
 * - Die gesetzten data-label-Attribute werden ausschließlich von
 *   Regeln innerhalb der Mobile-Media-Query gelesen.
 * - Beim Wechsel zurück auf Desktop-Breite wird der Drawer-Zustand
 *   sauber zurückgesetzt.
 */

const MOBILE_BREAKPOINT = 1024;

let isInitialized = false;

function isMobileViewport() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
}

function getElements() {
    return {
        toggle: document.getElementById('mobile-nav-toggle'),
        overlay: document.getElementById('mobile-nav-overlay'),
        sidebar: document.getElementById('sidebar')
    };
}

function openDrawer() {
    const { toggle, overlay, sidebar } = getElements();
    if (!sidebar) return;

    sidebar.classList.add('open');
    overlay?.classList.add('is-visible');
    overlay?.removeAttribute('hidden');
    toggle?.classList.add('is-open');
    toggle?.setAttribute('aria-expanded', 'true');
    toggle?.setAttribute('aria-label', 'Menü schließen');
    // Hintergrund nicht mitscrollen lassen, solange der Drawer offen ist
    document.body.style.overflow = 'hidden';
}

function closeDrawer() {
    const { toggle, overlay, sidebar } = getElements();
    if (!sidebar) return;

    sidebar.classList.remove('open');
    overlay?.classList.remove('is-visible');
    toggle?.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');
    toggle?.setAttribute('aria-label', 'Menü öffnen');
    document.body.style.overflow = '';
}

function toggleDrawer() {
    const { sidebar } = getElements();
    if (!sidebar) return;
    if (sidebar.classList.contains('open')) {
        closeDrawer();
    } else {
        openDrawer();
    }
}

/**
 * Ergänzt Tabellenzellen um data-label (Spaltenname aus dem thead),
 * damit die Mobile-Kartenansicht "Label: Wert" anzeigen kann.
 * Auf Desktop hat das Attribut keinerlei Wirkung.
 */
export function applyTableLabels(root = document) {
    // Datenquellen-Liste: CSS-Grid mit .table-list-header / .table-list-row
    root.querySelectorAll('.tables-list').forEach((list) => {
        const headerCells = list.querySelectorAll('.table-list-header .table-list-cell');
        if (headerCells.length === 0) return;

        const labels = Array.from(headerCells).map((cell) => (cell.textContent || '').trim());

        list.querySelectorAll('.table-list-row').forEach((row) => {
            row.querySelectorAll(':scope > .table-list-cell').forEach((cell, index) => {
                const label = labels[index];
                if (label && cell.getAttribute('data-label') !== label) {
                    cell.setAttribute('data-label', label);
                }
            });
        });
    });

    // Klassische <table>-Listen (falls an anderer Stelle verwendet)
    root.querySelectorAll('table.data-table').forEach((table) => {
        const headerCells = table.querySelectorAll('thead th');
        if (headerCells.length === 0) return;

        const labels = Array.from(headerCells).map((th) => (th.textContent || '').trim());

        table.querySelectorAll('tbody tr').forEach((row) => {
            Array.from(row.children).forEach((cell, index) => {
                const label = labels[index];
                if (label && !cell.hasAttribute('data-label')) {
                    cell.setAttribute('data-label', label);
                }
            });
        });
    });
}

/**
 * Initialisiert die Mobile-Navigation. Mehrfachaufrufe sind unschädlich.
 */
export function initMobileNav() {
    if (isInitialized) return;

    const { toggle, overlay } = getElements();
    if (!toggle) return; // Element fehlt (z.B. Landing-Page) → nichts tun

    isInitialized = true;

    toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        toggleDrawer();
    });

    overlay?.addEventListener('click', closeDrawer);

    // Nach der Navigation soll der Drawer zugehen
    document.addEventListener('click', (event) => {
        if (!isMobileViewport()) return;
        const navLink = event.target.closest('.nav-link');
        if (navLink) {
            closeDrawer();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeDrawer();
        }
    });

    // Zurück auf Desktop-Breite: Drawer-Zustand vollständig aufräumen,
    // damit keine Mobile-Klasse in die Desktop-Ansicht "durchblutet".
    window.addEventListener('resize', () => {
        if (!isMobileViewport()) {
            closeDrawer();
        }
    });

    // Tabellen-Labels initial und bei DOM-Änderungen nachziehen
    applyTableLabels();

    const pageContent = document.getElementById('page-content');
    if (pageContent && typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(() => applyTableLabels(pageContent));
        observer.observe(pageContent, { childList: true, subtree: true });
    }
}

export default { initMobileNav, applyTableLabels };
